import { useEffect } from 'react';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  FORMAT_TEXT_COMMAND,
  KEY_DOWN_COMMAND
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLinkDialog } from '../linkDialog';

/**
 * composer-v2 KeyboardShortcutsPlugin — KAP-304.
 *
 * Cmd/Ctrl+B and Cmd/Ctrl+I are handled natively by the browser's
 * `beforeinput` flow + Lexical's default rich-text registration; this
 * plugin owns the shortcuts that browsers do not bind by default:
 *
 *   Cmd+U       → underline (browsers do NOT toggle this in contenteditable
 *                  by default — left to the page)
 *   Cmd+Shift+X → strikethrough
 *   Cmd+E       → inline code
 *   Cmd+K       → open link popover (or strip link if already inside one)
 *
 * Registered at COMMAND_PRIORITY_HIGH so the dispatch beats RichTextPlugin's
 * own `KEY_DOWN_COMMAND` listeners (matches `EnterToSendPlugin` precedent).
 */

function isPrimaryModifier(event) {
  return event.metaKey || event.ctrlKey;
}

function captureSelectionRect() {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  const rect = range.getBoundingClientRect();
  if (!rect || (rect.width === 0 && rect.height === 0)) return null;
  return { top: rect.top, left: rect.left, bottom: rect.bottom, right: rect.right, width: rect.width, height: rect.height };
}

function captureSelectionText() {
  const selection = window.getSelection();
  return selection ? selection.toString() : '';
}

export function KeyboardShortcutsPlugin() {
  const [editor] = useLexicalComposerContext();
  const linkDialog = useLinkDialog();

  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event) => {
        if (!event || !isPrimaryModifier(event)) return false;

        const key = event.key.toLowerCase();

        if (key === 'u' && !event.shiftKey) {
          event.preventDefault();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
          return true;
        }

        if (key === 'x' && event.shiftKey) {
          event.preventDefault();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
          return true;
        }

        if (key === 'e' && !event.shiftKey) {
          event.preventDefault();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
          return true;
        }

        if (key === 'k' && !event.shiftKey) {
          event.preventDefault();
          let defaultUrl = '';
          let defaultText = '';
          editor.getEditorState().read(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              const text = selection.getTextContent();
              if (/^https?:\/\//i.test(text)) {
                defaultUrl = text;
              } else {
                defaultText = text;
              }
            }
          });
          if (!defaultUrl && !defaultText) {
            const selectedText = captureSelectionText();
            if (/^https?:\/\//i.test(selectedText)) {
              defaultUrl = selectedText;
            } else {
              defaultText = selectedText;
            }
          }
          linkDialog.open({
            anchorRect: captureSelectionRect(),
            defaultUrl,
            defaultText
          });
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor, linkDialog]);

  return null;
}
